import { useState, useMemo, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { Box, CircularProgress, Tab, Typography } from '@mui/material';
import { Dialog } from '@components/dialog/Dialog';
import { DocumentArchiveTable } from '@components/documentArchiveTable';
import { useGetPolicyDocumentArchive, useGetPolicy } from '@services/policy';
import { useStore } from '@store/GlobalStore';
import { shallow } from 'zustand/shallow';
import { PolicyTransactionTypesToLabel } from '@models/policyTransaction';
import { PolicyDocumentArchiveResponse } from '@lio/api-types';
import { filterDocumentsByVisibility, filterLatestDocuments, relabelForLio } from '@utils/documentVisibility';
import { Tabs } from '@components/tabs/Tabs';
import { NotesEntitiesSideMenu } from '@components/notesDialog/components/NotesEntitiesSideMenu';
import type { NotesEntityItem } from '@components/notesDialog/types';

type ArchiveTransaction = PolicyDocumentArchiveResponse['transactions'][number];

interface PolicyDocumentArchiveDialogProps {
  open: boolean;
  onClose: () => void;
  title: string;
}

export const PolicyDocumentArchiveDialog = ({ open, onClose, title }: PolicyDocumentArchiveDialogProps) => {
  const { policyId } = useParams();
  const [{ isInternalUser }, layoutPreferences] = useStore(
    (state) => [state.userAuth, state.layoutPreferences],
    shallow,
  );
  const clockFormat = layoutPreferences.clockFormat;
  const { archive, isLoading } = useGetPolicyDocumentArchive(policyId);
  const { policy } = useGetPolicy({ policyId });
  const isWritingCompanyLio = policy?.boundQuote?.writingCompany.productCode === 'LIO';

  const [activeTab, setActiveTab] = useState<'policy' | 'transactions'>('policy');
  const [selectedTransaction, setSelectedTransaction] = useState<NotesEntityItem | null>(null);

  const policyDocs = useMemo(
    () => relabelForLio(filterDocumentsByVisibility(archive?.policy ?? [], isInternalUser), isWritingCompanyLio),
    [archive?.policy, isInternalUser, isWritingCompanyLio],
  );
  const transactions = useMemo(() => archive?.transactions ?? [], [archive?.transactions]);

  const transactionItems: NotesEntityItem[] = useMemo(
    () =>
      transactions.map((tx) => ({
        id: tx.id,
        entity: 'transaction' as const,
        title:
          PolicyTransactionTypesToLabel[tx.transactionType as unknown as keyof typeof PolicyTransactionTypesToLabel] ??
          tx.transactionType,
        effectiveOn: tx.effectiveOn ?? undefined,
      })),
    [transactions],
  );

  useEffect(() => {
    if (activeTab === 'transactions' && !selectedTransaction && transactionItems.length > 0) {
      setSelectedTransaction(transactionItems[0]);
    }
  }, [activeTab, selectedTransaction, transactionItems]);

  const selectedTxData: ArchiveTransaction | undefined = transactions.find((tx) => tx.id === selectedTransaction?.id);
  const selectedTxDocs = useMemo(
    () =>
      selectedTxData
        ? relabelForLio(
            filterDocumentsByVisibility(filterLatestDocuments(selectedTxData.documents), isInternalUser),
            isWritingCompanyLio,
          )
        : [],
    [selectedTxData, isInternalUser, isWritingCompanyLio],
  );

  return (
    <Dialog
      open={open}
      onClose={onClose}
      handleCloseDialog={onClose}
      maxWidth='lg'
      fullWidth
      size='fullWidth'
      allowFullscreen
      title={title}
      sxProps={dialogSxProps}
    >
      <Box sx={sxProps.subHeader}>
        <Tabs
          type='miniShort'
          value={activeTab}
          onChange={(_, v) => {
            setActiveTab(v);
            if (v === 'transactions' && transactionItems.length > 0) {
              setSelectedTransaction(transactionItems[0]);
            } else {
              setSelectedTransaction(null);
            }
          }}
        >
          <Tab value='policy' label='Policy' />
          <Tab value='transactions' label='Transactions' />
        </Tabs>
      </Box>

      <Box sx={sxProps.bodyWrapper}>
        {activeTab === 'transactions' && (
          <NotesEntitiesSideMenu
            items={transactionItems}
            selectedId={selectedTransaction?.id ?? null}
            onSelect={(item) => setSelectedTransaction(item)}
            emptyLabel='No transactions'
            id='transactions-side-menu'
          />
        )}

        <Box sx={sxProps.mainContent}>
          {isLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress size={24} />
            </Box>
          ) : activeTab === 'policy' ? (
            <DocumentArchiveTable
              docs={policyDocs}
              isLoading={isLoading}
              clockFormat={clockFormat}
              noResultsMessage={!archive && !isLoading ? 'Unable to load policy documents' : 'No documents found'}
            />
          ) : selectedTxData ? (
            <DocumentArchiveTable
              docs={selectedTxDocs}
              isLoading={false}
              clockFormat={clockFormat}
              noResultsMessage='No documents for this transaction'
            />
          ) : (
            <Typography variant='bodySmall' color='text.subdued'>
              {transactions.length === 0 ? 'No transactions found' : 'Select a transaction to view documents'}
            </Typography>
          )}
        </Box>
      </Box>
    </Dialog>
  );
};

const dialogSxProps = {
  dialogContent: {
    height: '100%',
    overflow: 'hidden',
    display: 'flex',
    flexDirection: 'column',
    flex: 1,
    minHeight: 0,
    pt: 0,
  },
  paperSx: {
    overflow: 'hidden',
  },
};

const sxProps = {
  subHeader: {
    pl: 1,
    pr: 0.5,
    py: 0.5,
    display: 'flex',
    alignItems: 'center',
    borderBottom: '1px solid',
    borderColor: 'border.default',
    '.MuiTabs-root': {
      padding: '0px !important',
      margin: '0 !important',
    },
  },
  bodyWrapper: {
    overflow: 'hidden',
    display: 'flex',
    flex: 1,
    minHeight: 0,
  },
  mainContent: {
    flex: 1,
    overflow: 'auto',
    p: 2,
  },
};
